import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Tweens a number towards `target` so the score climbs instead of snapping.
 *
 * Two decisions here are load-bearing rather than cosmetic.
 *
 * **It is seeded with `target`, so the first render already shows the final
 * value, and only *changes* animate.** A count-up that started at zero would
 * mean the score ring rendered `0` on mount and reached `93` a beat later. That
 * breaks any synchronous read of the number — `ScoreRing.test.tsx` asserts
 * `getByText('72')` immediately after render, with no `waitFor` — and it is also
 * the worse behaviour: the ring mounts the moment the profile has any content at
 * all, so a mount animation would fire once, on a number nobody was watching.
 * Seeding puts the motion exactly where the reward is, on every keystroke after.
 *
 * **The caller must keep announcing the real `target`, not this value.** Feeding
 * a tweening number into an `aria-live` region would queue one announcement per
 * frame. The tween is for the eye; the live region gets the final figure.
 *
 * When motion is off — either because the user asked for less of it or because
 * there is no `requestAnimationFrame` to drive it — `target` is returned
 * directly rather than mirrored into state. That keeps the hook a pass-through
 * in that mode instead of a state machine that has to be kept in sync.
 */
export function useCountUp(target: number, durationMs = 650): number {
  const reduced = usePrefersReducedMotion();
  const [tweened, setTweened] = useState(target);
  // What is currently on screen. A ref rather than reading `tweened`, so the
  // effect can pick the tween up from wherever the last one got to without
  // listing it as a dependency — which would restart the animation on every
  // frame it produced.
  const shown = useRef(target);
  const animates = !reduced && typeof requestAnimationFrame === 'function';

  useEffect(() => {
    if (!animates) {
      // Keep the ref honest anyway: if the preference is switched back on later,
      // the next tween should start from what is on screen, not from whatever
      // was last animated to.
      shown.current = target;
      return;
    }

    const from = shown.current;
    if (from === target) return;

    let frame = 0;
    let start: number | null = null;

    const step = (now: number) => {
      start ??= now;
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      // Same easing curve as the ring's stroke transition, so the number and
      // the arc arrive together instead of drifting apart.
      const eased = 1 - Math.pow(1 - t, 4);
      const next = Math.round(from + (target - from) * eased);
      shown.current = next;
      setTweened(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs, animates]);

  return animates ? tweened : target;
}
